import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import RootPanel from './RootPanel';
import KnowledgeAdmin from './KnowledgeAdmin';
import './RootWorkspace.css';

// ═══════════════════════════════════════════════════════════
//  RootWorkspace — root-only admin surface.
//
//  A full dark-chrome surface (not a tab of the Developers dashboard).
//  Three sections:
//    · Фидбэк      — ALL feedback ever left, with context chips
//                    (постер / оценка / общий), file downloads, delete;
//    · База знаний — the estimator knowledge base (approve/reject);
//    · Пользователи — the existing account admin (RootPanel).
// ═══════════════════════════════════════════════════════════

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

type Section = 'feedback' | 'knowledge' | 'users';

const SECTIONS: { key: Section; label: string }[] = [
  { key: 'feedback', label: 'Фидбэк' },
  { key: 'knowledge', label: 'База знаний' },
  { key: 'users', label: 'Пользователи' },
];

// ── Feedback types (entries now carry optional project/estimate context) ──
interface FeedbackFile {
  id: string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  created_at: string;
}

interface FeedbackEntry {
  id: string;
  author_id: string | null;
  author_name: string;
  title: string;
  body: string;
  created_at: string;
  updated_at: string;
  files: FeedbackFile[];
  project_id: string | null;
  project_name: string | null;
  estimate_id: string | null;
  estimate_title: string | null;
}

interface Toast {
  id: number;
  msg: string;
  kind: 'ok' | 'err';
}

const ERROR_RU: Record<string, string> = {
  not_found: 'Запись не найдена',
  forbidden: 'Недостаточно прав',
};

async function readError(res: Response): Promise<string> {
  const data = await res.json().catch(() => ({} as any));
  return ERROR_RU[data.error as string] || data.message || data.error || `Ошибка (${res.status})`;
}

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Feedback section: read + delete of ALL entries with context chips ──
const RootFeedback: React.FC = () => {
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const pushToast = useCallback((msg: string, kind: 'ok' | 'err') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, kind }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4200);
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(`${API}/api/feedback`);
      if (!res.ok) {
        setLoadError(await readError(res));
        setEntries([]);
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch {
      setLoadError('Не удалось загрузить фидбэк — проверьте соединение.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deleteEntry = async (entry: FeedbackEntry) => {
    if (!window.confirm(`Удалить фидбэк «${entry.title}»? Файлы тоже пропадут.`)) return;
    try {
      const res = await fetch(`${API}/api/feedback/${entry.id}`, { method: 'DELETE' });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      pushToast('Фидбэк удалён', 'ok');
    } catch {
      pushToast('Сетевая ошибка при удалении', 'err');
    }
  };

  const downloadFile = async (entryId: string, file: FeedbackFile) => {
    try {
      const res = await fetch(`${API}/api/feedback/${entryId}/files/${file.id}/download`);
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      pushToast('Не удалось скачать файл', 'err');
    }
  };

  return (
    <div className="rw-feedback">
      {loading ? (
        <div className="rw-notice">Загрузка…</div>
      ) : loadError ? (
        <div className="rw-notice rw-notice-err">
          {loadError}
          <button className="btn-secondary rw-retry" onClick={load}>
            Повторить
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="rw-empty">Фидбэка пока нет.</div>
      ) : (
        <div className="rw-fb-list">
          {entries.map((entry) => (
            <article className="rw-fb-card" key={entry.id}>
              <header className="rw-fb-head">
                <h3 className="rw-fb-title">{entry.title}</h3>
                <button
                  className="rw-icon-btn rw-icon-danger"
                  title="Удалить"
                  onClick={() => deleteEntry(entry)}
                >
                  🗑
                </button>
              </header>

              <div className="rw-fb-context">
                {entry.project_id ? (
                  <span className="rw-ctx-chip rw-ctx-poster">
                    Постер: {entry.project_name || 'без имени'}
                  </span>
                ) : null}
                {entry.estimate_id ? (
                  <span className="rw-ctx-chip rw-ctx-estimate">
                    Оценка: {entry.estimate_title || 'без названия'}
                  </span>
                ) : null}
                {!entry.project_id && !entry.estimate_id && (
                  <span className="rw-ctx-chip rw-ctx-general">Общий</span>
                )}
              </div>

              <div className="rw-fb-meta">
                <span className="rw-fb-author">{entry.author_name}</span>
                <span className="rw-fb-dot">·</span>
                <span className="rw-fb-date">{fmtDate(entry.created_at)}</span>
              </div>

              {entry.body && <div className="rw-fb-body">{entry.body}</div>}

              {entry.files.length > 0 && (
                <div className="rw-fb-files">
                  {entry.files.map((file) => (
                    <button
                      key={file.id}
                      type="button"
                      className="rw-file-chip"
                      title="Скачать"
                      onClick={() => downloadFile(entry.id, file)}
                    >
                      <span className="rw-file-name">{file.filename}</span>
                      <span className="rw-file-size">{fmtSize(file.size_bytes)}</span>
                    </button>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {toasts.length > 0 && (
        <div className="rw-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`rw-toast rw-toast-${t.kind}`}>
              {t.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RootWorkspace: React.FC<{ onExit?: () => void }> = ({ onExit }) => {
  const { user, logout } = useAuth();
  const [section, setSection] = useState<Section>('feedback');

  return (
    <div className="app root-workspace">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">Lato</h1>
          <span className="app-subtitle">Root — фидбэк, база знаний, пользователи</span>
        </div>
        <div className="header-right">
          {onExit && (
            <button className="header-architect-link" onClick={onExit} title="К выбору воркспейса">
              ← Воркспейсы
            </button>
          )}
          {user && (
            <span className="user-chip" title={user.email}>
              <span className="user-chip-avatar">{(user.displayName || user.username).substring(0, 2)}</span>
              <span className="user-chip-name">{user.displayName || user.username}</span>
              {user.isRoot && <span className="user-chip-role">root</span>}
              <button className="user-chip-logout" onClick={logout} title="Выйти">× Выйти</button>
            </span>
          )}
        </div>
      </header>

      <div className="tabs rw-tabs">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            className={`tab ${section === s.key ? 'tab-active' : ''}`}
            onClick={() => setSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="main-layout layout-full">
        <div className="main-content">
          <motion.div
            key={section}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {section === 'feedback' && <RootFeedback />}
            {section === 'knowledge' && <KnowledgeAdmin />}
            {section === 'users' && <RootPanel />}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RootWorkspace;
